// src/pages/AdminPage/hooks/usePaginatedData.ts
import { useState, useEffect, useCallback } from "react";
import type { AxiosResponse } from "axios";
import type { PaginatedResponse } from "../utils/adminTypes";

interface UsePaginatedDataOptions<T> {
  fetcher: (
    page: number,
    limit: number,
    search: string,
  ) => Promise<AxiosResponse<PaginatedResponse<T>>>;
  limit?: number;
  enabled?: boolean;
  onError?: (msg: string) => void;
}

export function usePaginatedData<T>({
  fetcher,
  limit = 20,
  enabled = true,
  onError,
}: UsePaginatedDataOptions<T>) {
  const [data, setData] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const res = await fetcher(page, limit, search);
      setData(res.data.data || []);
      setTotal(res.data.meta?.total || 0);
    } catch {
      onError?.("Ma'lumotlar yuklanmadi");
    } finally {
      setLoading(false);
    }
  }, [fetcher, page, limit, search, enabled, onError]);

  // Debounce search (300ms) — qidirayotganda har harfda API chaqirmaydi
  useEffect(() => {
    const timer = setTimeout(
      () => {
        load();
      },
      search ? 300 : 0,
    );
    return () => clearTimeout(timer);
  }, [load]);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(1); // search'da page 1 ga qaytadi
  }, []);

  return {
    data,
    total,
    page,
    setPage,
    search,
    setSearch: handleSearch,
    loading,
    reload: load,
  };
}
